const HERO_IMAGE =
  'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?auto=format&fit=crop&w=1600&q=80';

/** Featured guide: intermediate goalkeeper practice — warm-up, wall setup, and session ideas */
export const INTERMEDIATE_GOALKEEPER_PRACTICE_POST = {
  id: 'featured-intermediate-goalkeeper-practice',
  slug: 'intermediate-soccer-goalkeeper-practice-warm-up-wall-setup',
  title: 'Intermediate Goalkeeper Practice: Warm-Up, Wall Setup & Session Ideas',
  excerpt:
    'You already know the basics—now sharpen match-day habits. A full warm-up routine, step-by-step free-kick wall setup, and intermediate drills with free YouTube sessions.',
  publishedAt: '2026-07-30T00:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'GK Training Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'More goalkeeper & position guides',
  relatedPosts: [
    {
      slug: 'goalkeeper-achilles-soleus-pain-blood-flow-loading',
      title:
        'Goalkeeper Achilles & Soleus Pain: Blood-Flow Warm-Up & Gradual Tendon Loading',
      blurb: 'mild pain prep — circulation and tendon loading, not aggressive stretching.',
    },
    {
      slug: 'soccer-goalkeeper-drills-free-youtube-videos',
      title: 'Soccer Goalkeeper Drills + Free YouTube Training Videos',
      blurb: 'full drill library for all levels.',
    },
    {
      slug: 'soccer-defender-drills-free-youtube-videos',
      title: 'Soccer Defender Drills + Free YouTube Training Videos',
      blurb: 'help your wall players understand their roles.',
    },
    {
      slug: '7v7-soccer-formation-2-3-1-guide',
      title: '7v7 Soccer Formation: The 2-3-1 Guide',
      blurb: 'GK role in a common youth shape.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '15 min', label: 'Match-ready warm-up' },
        { value: 'Wall setup', label: 'Step-by-step + size chart' },
        { value: '12+', label: 'Intermediate drill ideas' },
        { value: '10', label: 'Free YouTube sessions' },
      ],
    },
    {
      type: 'panel',
      title: 'Who this guide is for',
      paragraphs: [
        'This is written for intermediate keepers—club players ages 12–18, adult rec league starters, or anyone who has moved past beginner handling but still wants structure. You can catch, dive, and distribute; now the focus shifts to speed of decision-making, commanding your box, and organizing set pieces under pressure.',
        'Each section pairs field drills with hand-picked YouTube videos. Run these solo with a rebounder, with one server, or in a small-group GK block at the end of team practice.',
      ],
      chips: ['Intermediate level', 'Warm-up included', 'Wall defense', 'Free on YouTube'],
    },
    {
      type: 'panel',
      title: '1 — Match-ready warm-up (15 minutes)',
      paragraphs: [
        'Intermediate keepers need more than light jogging. Your warm-up should raise heart rate, activate hips and shoulders, and finish with live balls at increasing speed—so your first match touch is not your first dive of the day.',
      ],
      orderedList: [
        '3 min — Dynamic movement: high knees, butt kicks, lateral shuffles, and arm circles. No static stretching yet.',
        '4 min — Footwork activation: tape a 4-cone square; shuffle → forward → shuffle → back, 3 reps each direction. Add a ball at your feet for the last 2 minutes.',
        '3 min — Handling ladder: kneeling catches (chest & waist height) → standing catches → light collapse saves. Server starts 8 yds out, then 12 yds.',
        '3 min — Angle touches: quick steps to near post, center, and far post from set position. Finish in ready shape every time.',
        '2 min — One hard shot per side at 70% power. Full dive only if body feels loose—save max effort for the session.',
      ],
      callout:
        'Pre-match tip: after this warm-up, face 2–3 corners and 1 free kick with a live wall so your first set piece in the game is not a surprise.',
    },
    {
      type: 'videoList',
      title: 'YouTube — goalkeeper warm-ups',
      videos: [
        {
          title: '20 Goalkeeper Warm-Up Drills | Pro GK',
          channel: 'Pro GK Academy (Omar Zeenni)',
          description:
            'Compilation of pro-level activation drills—footwork, handling, and movement you can cherry-pick for a 15-minute routine.',
          url: 'https://www.youtube.com/watch?v=Qj5wJN04GGQ',
        },
        {
          title: 'Add this to your Goalkeeper Warmup',
          channel: 'Pro GK Academy',
          description: 'Short activation sequence to bolt onto the start of any session or pre-match routine.',
          url: 'https://www.youtube.com/watch?v=3p98eaLJ79w',
        },
        {
          title: 'Warmup Activation',
          channel: 'Pro GK Academy',
          description: 'Pre-match activation focused on sharp feet and ready shape before live shots.',
          url: 'https://www.youtube.com/watch?v=8rzUoUu8by4',
        },
        {
          title: 'The BEST goalkeeping drills to help you warm up',
          channel: 'Prime Focus Goalkeeping',
          description: 'Simple, effective warm-up exercises for keepers of every age before training and games.',
          url: 'https://www.youtube.com/watch?v=l5_CiyNgjVU',
        },
      ],
    },
    {
      type: 'panel',
      title: '2 — Free-kick wall setup: your job as commander',
      paragraphs: [
        'At intermediate level, organizing the wall is as important as making the save. You are the architect: decide how many players, where they stand, and which side of the goal you cover. The wall blocks the near side; you take the far post and anything over the wall.',
        'Work fast—aim to set the wall within 10 seconds. Ask the referee for "on my whistle" if you need time. Assign an anchorman before the match: one defender faces you, listens to your left/right commands, then turns to face the ball when you signal.',
      ],
      orderedList: [
        'Step 1 — Spot the ball and call the number: shout how many players you need (see chart below).',
        'Step 2 — Stand at the post the wall will protect. Line yourself up between the ball and the center of the goal.',
        'Step 3 — Place the first wall player about one body-width outside the near post—not directly on the post, or curlers sneak inside.',
        'Step 4 — Add players shoulder-to-shoulder. Put your tallest players in the middle (positions 2–3) to block shots over the wall.',
        'Step 5 — Shift the whole wall left or right with hand signals until the near post is covered.',
        'Step 6 — Take your position on the open side. Peek around the wall to see the ball; stay on your toes.',
        'Step 7 — Final check: can the ball reach the near post without going over the wall? If yes, shuffle the wall tighter.',
      ],
      list: [
        'Quick free kick: station one defender in front of the ball immediately to buy time while you organize.',
        'Jumping wall vs. standing wall: agree with your team in training—every other player jumps, or everyone holds and stays tall on toes.',
        'Draught excluder: some teams place one player on the ground behind a jumping wall to block driven shots under the jump.',
        'Indirect free kick in the box: wall must stay on the goal line until the ball is struck—different rules, same loud communication.',
      ],
    },
    {
      type: 'table',
      title: 'How many players in the wall?',
      intro:
        'Use this as a starting point. Adjust for the kicker—if they love curling the near post, add one more player or tuck the wall tighter.',
      headers: ['Ball position', 'Distance from goal', 'Typical wall size', 'Goalkeeper focus'],
      rows: [
        ['Central, edge of box', '18 yds', '4–5 players', 'Far post; watch for dip over wall'],
        ['Central, top of D', '22–25 yds', '3–4 players', 'Over the wall and far corner'],
        ['Wide angle (near touchline)', '18–22 yds', '2–3 players', 'Near post; be ready for cross or cut-back'],
        ['Wide angle (near corner flag)', '20+ yds', '1–2 players or post cover', 'Command box; deal with cross or far-post run'],
        ['Very close (<15 yds)', 'Inside box', '5–6 players + draught excluder', 'Low driven shots; wall height critical'],
      ],
    },
    {
      type: 'panel',
      title: '3 — Wall defense drills (run in training)',
      list: [
        'Wall walk-through: no ball—keeper calls "three wall, shift left" from five different spots on the pitch. Timed: under 10 seconds each.',
        'Live free kick: server takes 8–10 direct shots from central D. Keeper sets wall, then saves. Rotate wall players so everyone learns the anchorman role.',
        'Over-the-wall reaction: wall jumps on whistle; server strikes over or around. Keeper must read the kicker\'s body shape early.',
        'Quick kick prevention: attacker places ball; one defender must stand in front immediately. Keeper organizes behind them.',
        'Second-ball scramble: after save or parry, two attackers crash for the rebound. Keeper communicates "keeper" or "away."',
        'Wide free kick: only a 2-man wall—keeper deals with near-post curl or cross to the back post.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — free kicks & wall positioning',
      videos: [
        {
          title: "Why Goalkeepers Don't Stand In The Middle in Free Kicks",
          channel: 'Header',
          description:
            'Quick visual on why the wall covers one side and the keeper takes the other—essential context for intermediate players.',
          url: 'https://www.youtube.com/watch?v=8LNJ1bnxTCI',
        },
        {
          title: 'Do free-kick walls do more harm than good?',
          channel: 'BBC Sport / pundit discussion',
          description:
            'Debate on wall positioning and sightlines—useful for understanding when to trust the wall vs. when to see the ball clearly.',
          url: 'https://www.youtube.com/watch?v=4wwc_jpGFi0',
        },
        {
          title: 'Victor Valdes - Best Goalkeeper Training & Warm Up',
          channel: 'Martin football',
          description:
            'Pro-level warm-up including handling and footwork—good reference for how elite keepers prepare before a session.',
          url: 'https://www.youtube.com/watch?v=d7--q0jaKNE',
        },
      ],
    },
    {
      type: 'panel',
      title: '4 — Intermediate shot stopping & angles',
      list: [
        'Three-cone arc: server moves along an arc 14–18 yds out; you set before every strike. Emphasize last explosive step and set shape.',
        'Angle recovery: after a near-post save, spring to far post for a second ball within 3 seconds.',
        'Deflection drill: server shoots through a mannequin or teammate\'s legs—you read late movement.',
        'High-ball timing: server tosses or chips; attack the ball at its highest point with top hand over.',
        '1v1 patience: close space without diving early. Wait for a heavy touch, then smother or block.',
        'Cross and punch: whipped service from both flanks—call "keeper" or "away" loud enough for the whole box to hear.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — intermediate shot stopping',
      videos: [
        {
          title: '5 Angle & Reaction Drills | LAFC 2 Goalkeeper Training | Full Session',
          channel: 'Pro GK Academy',
          description:
            'Acceleration, deceleration, and angled shot stopping used with pro keepers—ideal intermediate session template.',
          url: 'https://www.youtube.com/watch?v=PzdSZhsi1Oo',
        },
        {
          title: 'Full Handling & Shot Stopping Session | 60 in 5 Goalkeeper Training',
          channel: 'Pro GK Academy',
          description: 'Progressive session from basic catches through game-speed shot stopping.',
          url: 'https://www.youtube.com/watch?v=6ebJt9soY_s',
        },
        {
          title: 'How to save EVERY 1v1 as a Goalkeeper',
          channel: 'RushGK',
          description: 'Positioning, patience, and spread saves when an attacker breaks through.',
          url: 'https://www.youtube.com/watch?v=bs1SEca_JW8',
        },
      ],
    },
    {
      type: 'panel',
      title: '5 — Distribution & playing out (intermediate habits)',
      list: [
        'Bowling to targets: 10 rolls each to left and right fullback spots—on the ground, no bounce.',
        'Driven pass: 8–10 yds through the center; progress to 20 yds to a midfielder checking in.',
        'Quick throw: after a catch, release to a wide player within 4 seconds—match tempo.',
        'Goal kick under pressure: receive imaginary press, take one touch, play to the open side.',
        'Voice as a weapon: call "back," "turn," or "man on" before every distribution—your defenders should hear you over the crowd.',
      ],
    },
    {
      type: 'videoList',
      title: 'YouTube — distribution',
      videos: [
        {
          title: 'The COMPLETE Goalkeeper Distribution Tutorial',
          channel: "Conor O'Keefe / ARS Goalkeeping",
          description:
            'Eight techniques: short pass, driven pass, clip, goal kick, roll, throw, side volley, and half volley.',
          url: 'https://www.youtube.com/watch?v=Y_Oll67U9IQ',
        },
      ],
    },
    {
      type: 'panel',
      title: '6 — Sample 75-minute intermediate session',
      orderedList: [
        '15 min — Match-ready warm-up (section 1).',
        '15 min — Angle shot stopping: three-cone arc + deflection drill (section 4).',
        '15 min — Wall walk-through, then 8 live free kicks from central D (sections 2–3).',
        '10 min — 1v1 situations: 6 reps each side (section 4 video).',
        '10 min — Distribution: rolls, throws, and 5 goal kicks under time pressure.',
        '10 min — Crosses: 6 services from each flank; punch, catch, or command "away."',
      ],
      callout:
        'For a shorter 45-minute block, cut crosses and run wall drills + angle work only.',
    },
    {
      type: 'panel',
      title: 'Equipment & match-day checklist',
      list: [
        'Two pairs of gloves (swap at halftime in wet conditions).',
        'Cones, tape for footwork box, and 6–8 balls.',
        'Pre-assign your wall anchorman and backup before kickoff.',
        'Agree wall jump vs. stand strategy with your back line in the pre-match huddle.',
        'Optional: mannequin or pop-up target for deflection and distribution drills.',
      ],
    },
  ],
  referenceLinks: [
    {
      label: 'The Goalkeepers\' View — How to set up a wall',
      url: 'https://www.thegoalkeepersview.com/blog-1/how-to-set-up-a-wall-a-goalkeepers-guide',
    },
    {
      label: 'Goalkeeping Matters — Defending free kicks and building a wall',
      url: 'https://goalkeepingmatters.com/2019/07/10/set-pieces-defending-freekicks-and-building-a-wall/',
    },
    {
      label: 'LIGAgk — How to set up the wall',
      url: 'https://ligagk.com/how-to-set-up-the-wall-goalkeeper/',
    },
    {
      label: 'Coaching Soccer Goalkeeping — Game-ready GK warm-up',
      url: 'https://coachingsoccergoalkeeping.com/game-ready-gk-warm-up/',
    },
    {
      label: 'Pro GK Academy — YouTube channel',
      url: 'https://www.youtube.com/@progkacademy',
    },
    {
      label: 'ARS Goalkeeping — YouTube channel',
      url: 'https://www.youtube.com/@ARSGoalkeeping',
    },
  ],
};
